import type { ReactElement, ReactNode } from 'react';
import type { Issue } from '@tallyframe/core';
import { ApiError } from '../api/errors';
import { Banner as UiBanner } from '../ui/Banner';
import { IssueList } from './IssueList';

export type BannerTone = 'danger' | 'warn' | 'info';

export function Banner(props: {
  tone: BannerTone;
  title: string;
  children?: ReactNode;
  issues?: Issue[];
  actions?: ReactNode;
  testId?: string;
  errorCode?: string;
  /** `assertive` for something that just failed, `polite` for background news. */
  live?: 'assertive' | 'polite' | 'off';
}): ReactElement {
  return (
    <div data-testid={props.testId} data-error-code={props.errorCode}>
      <UiBanner tone={props.tone} title={props.title} actions={props.actions} live={props.live}>
        {props.children}
        {props.issues && props.issues.length > 0 ? <IssueList issues={props.issues} /> : null}
      </UiBanner>
    </div>
  );
}

/**
 * The standard presentation of a failed request.
 *
 * `INTERNAL` and the export faults are shown differently on purpose: they are
 * not the caller's fault, so the banner does not invite somebody to go and
 * correct input that was already correct.
 */
export function ErrorBanner(props: {
  error: unknown;
  title?: string;
  actions?: ReactNode;
  testId?: string;
}): ReactElement | null {
  if (!props.error) return null;

  if (ApiError.isApiError(props.error)) {
    const { error } = props;
    const isInternalError = error.code === 'INTERNAL';
    return (
      <Banner
        tone="danger"
        title={props.title ?? (error.isCallerFixable ? 'That request was rejected' : 'The server could not complete that')}
        issues={isInternalError ? [] : error.issues}
        actions={props.actions}
        testId={props.testId ?? 'error-banner'}
        errorCode={error.code}
      >
        <>
          {isInternalError ? 'Something went wrong on our end. Try again.' : error.message}
          {error.isCallerFixable || isInternalError ? null : ' This is a fault on the server side, not something wrong with what you sent.'}
        </>
      </Banner>
    );
  }

  const message = props.error instanceof Error ? props.error.message : String(props.error);
  return (
    <Banner
      tone="danger"
      title={props.title ?? 'Something went wrong'}
      actions={props.actions}
      testId={props.testId ?? 'error-banner'}
      errorCode="INTERNAL"
    >
      {message}
    </Banner>
  );
}
